import { Fragment, useMemo } from 'react';
import { ArrowDownTrayIcon, CheckCircleIcon, ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useStoreroomHistory, useVanItemHistory } from '../hooks/useStockLevels';

// Where a Stock Levels figure came from.
//
// Read like a bank statement, oldest first: the opening count, then every
// delivery, pick, transfer and correction with the balance after each, landing
// on today's number. For a storeroom the weekly counts sit in the same
// timeline, each set against what the ledger said at that moment, which is
// the part that settles "the numbers are wrong" one way or the other.

const num = (v) => Number(v || 0).toLocaleString('en-GB', { maximumFractionDigits: 2 });
const signed = (v) => `${Number(v) > 0 ? '+' : ''}${num(v)}`;
const when = (d) => (d ? new Date(d).toLocaleString('en-GB', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London',
}).replace(',', '') : '—');
const ukDay = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { timeZone: 'Europe/London' }) : '—');

const TONE = {
  opening: 'bg-gray-100 text-gray-700',
  seed_correction: 'bg-gray-100 text-gray-700',
  receipt: 'bg-emerald-50 text-emerald-700',
  transfer_in: 'bg-emerald-50 text-emerald-700',
  returned: 'bg-emerald-50 text-emerald-700',
  grn_correction: 'bg-amber-50 text-amber-700',
  issue_request: 'bg-brand-light text-brand-dark',
  issue_transfer: 'bg-brand-light text-brand-dark',
  transfer_out: 'bg-sky-50 text-sky-700',
  manual: 'bg-amber-50 text-amber-700',
  issue_reversed: 'bg-amber-50 text-amber-700',
  // van kinds
  usage: 'bg-brand-light text-brand-dark',
  faulty: 'bg-red-50 text-red-700',
  adjustment: 'bg-amber-50 text-amber-700',
};

// The order the sum reads in on the proof line.
const SUMMARY_ORDER = ['opening', 'seed_correction', 'receipt', 'grn_correction', 'transfer_in', 'returned',
  'issue_request', 'issue_transfer', 'transfer_out', 'issue_reversed', 'manual', 'other'];

function csvCell(c) {
  const s = c === null || c === undefined ? '' : String(c);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function download(name, lines) {
  const url = URL.createObjectURL(new Blob([lines.map((r) => r.map(csvCell).join(',')).join('\n')],
    { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

function Diff({ value }) {
  const v = Number(value);
  if (v === 0) return <span className="font-semibold text-emerald-700">0</span>;
  return <span className={`font-semibold ${Math.abs(v) >= 5 ? 'text-red-700' : 'text-amber-700'}`}>{signed(v)}</span>;
}

function Shell({ title, subtitle, onClose, onCsv, children }) {
  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/40" onClick={onClose}>
      <div
        className="flex h-full w-full max-w-5xl flex-col bg-gray-50 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={title}
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 bg-white px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-gray-900">Stock history</h2>
            <p className="truncate text-sm text-gray-600">{title}</p>
            {subtitle ? <p className="text-xs text-gray-400">{subtitle}</p> : null}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {onCsv ? (
              <button
                type="button"
                onClick={onCsv}
                className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:border-brand hover:text-brand-dark"
              >
                <ArrowDownTrayIcon className="h-4 w-4" /> CSV
              </button>
            ) : null}
            <button type="button" onClick={onClose} className="rounded-md p-1.5 text-gray-400 hover:text-gray-700" aria-label="Close">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------ STOREROOM

function StoreroomHistory({ storeroomId, codes, name, onClose }) {
  const { data, isLoading, isError, error } = useStoreroomHistory(storeroomId, codes);
  const multi = codes.length > 1;

  // Weekly counts slotted into the timeline at the moment they were submitted.
  const timeline = useMemo(() => {
    if (!data) return [];
    const rows = data.movements.map((m) => ({ type: 'move', at: new Date(m.at).getTime(), m }));
    data.counts.filter((c) => !c.is_opening).forEach((c) => {
      rows.push({ type: 'count', at: new Date(c.submitted_at).getTime(), c });
    });
    return rows.sort((a, b) => a.at - b.at || (a.type === 'count' ? 1 : -1));
  }, [data]);

  const csv = () => {
    if (!data) return;
    const lines = [['Date', 'What', 'Code', 'Reference', 'Detail', 'Booked by', 'Qty', 'Balance', 'Counted', 'Ledger at count', 'Difference']];
    timeline.forEach((r) => {
      if (r.type === 'move') {
        const { m } = r;
        lines.push([m.count_day ? m.count_day.split('-').reverse().join('/') : when(m.at), m.label, m.item_code, m.reference, m.detail, m.by, m.qty, m.balance, m.counted ?? '', '', '']);
      } else {
        const { c } = r;
        lines.push([when(c.submitted_at), `Week ${c.count_week} count #${c.count_id}`, c.codes.join(' + '), '', `counted by ${c.counted_by || '—'}`, c.counted_by, '', '', c.counted, c.ledger_at_count, c.difference]);
      }
    });
    lines.push([]);
    lines.push(['On hand now', '', '', '', '', '', '', data.on_hand]);
    download(`stock-history-${data.storeroom.name}-${codes.join('+')}.csv`.replace(/\s+/g, '-'), lines);
  };

  const parts = data ? SUMMARY_ORDER.filter((k) => data.summary[k]).map((k) => ({ k, label: data.labels[k], v: data.summary[k] })) : [];

  return (
    <Shell
      title={`${codes.join(' + ')} · ${name || ''}`}
      subtitle={data ? data.storeroom.name : null}
      onClose={onClose}
      onCsv={data ? csv : null}
    >
      {isLoading ? <p className="py-10 text-center text-sm text-gray-400">Loading history…</p> : null}
      {isError ? (
        <p className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
          {error?.response?.data?.error || 'The history could not be loaded.'}
        </p>
      ) : null}

      {data ? (
        <>
          {/* The sum, written out. Every figure is a total of rows below. */}
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-2 text-sm">
              {parts.length ? parts.map((p, i) => (
                <span key={p.k} className="inline-flex items-baseline gap-1.5">
                  {i > 0 ? <span className="text-gray-400">{p.v < 0 ? '−' : '+'}</span> : null}
                  <span className="text-gray-500">{p.label}</span>
                  <span className="font-semibold tabular-nums text-gray-900">{num(i > 0 ? Math.abs(p.v) : p.v)}</span>
                </span>
              )) : <span className="text-gray-500">No movements recorded</span>}
              <span className="text-gray-400">=</span>
              <span className="inline-flex items-baseline gap-1.5">
                <span className="text-gray-500">On hand now</span>
                <span className="text-lg font-bold tabular-nums text-brand">{num(data.on_hand)}</span>
              </span>
            </div>
            <p className={`mt-2 flex items-center gap-1.5 text-xs ${data.matches ? 'text-emerald-700' : 'text-red-700'}`}>
              {data.matches
                ? <><CheckCircleIcon className="h-4 w-4" /> The on-hand figure is exactly the sum of the {num(data.movements.length)} ledger rows below. Nothing was set by hand outside them.</>
                : <><ExclamationTriangleIcon className="h-4 w-4" /> The on-hand figure ({num(data.on_hand)}) does not match the ledger rows ({num(data.ledger_total)}). Report this to IT.</>}
            </p>
          </div>

          {/* Counts against the ledger. */}
          <div className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div className="border-b border-gray-100 px-4 py-2">
              <h3 className="text-sm font-semibold text-gray-700">Counts compared with the ledger</h3>
              <p className="text-xs text-gray-400">
                What was counted, against what the ledger said when that count was submitted.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-3 py-2">Count</th>
                    <th className="px-3 py-2">Submitted</th>
                    <th className="px-3 py-2">Counted by</th>
                    <th className="px-3 py-2 text-right">Counted</th>
                    <th className="px-3 py-2 text-right">Ledger said</th>
                    <th className="px-3 py-2 text-right">Difference</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.counts.length ? data.counts.map((c) => (
                    <tr key={c.count_id}>
                      <td className="px-3 py-2 text-gray-700">
                        Week {c.count_week} <span className="text-xs text-gray-400">#{c.count_id}</span>
                        {multi && c.codes.length < codes.length ? (
                          <span className="block text-xs text-gray-400">{c.codes.join(' + ')} only</span>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 tabular-nums text-gray-600">{when(c.submitted_at)}</td>
                      <td className="px-3 py-2 text-gray-600">{c.counted_by || '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold text-gray-900">{num(c.counted)}</td>
                      {c.is_opening ? (
                        <td colSpan={2} className="px-3 py-2 text-right text-xs text-gray-500">
                          Opening balance taken from this count
                        </td>
                      ) : (
                        <>
                          <td className="px-3 py-2 text-right tabular-nums text-gray-700">{num(c.ledger_at_count)}</td>
                          <td className="px-3 py-2 text-right tabular-nums"><Diff value={c.difference} /></td>
                        </>
                      )}
                    </tr>
                  )) : (
                    <tr><td colSpan={6} className="px-3 py-4 text-center text-xs text-gray-400">No storeroom counts of this item since the ledger started.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="border-t border-gray-100 px-4 py-2 text-xs text-gray-400">
              A plus means more was on the shelf than the ledger expected; a minus means less. Anything picked or
              received between walking the shelf and submitting the count also shows up as a difference, so check the
              rows either side of it below.
            </p>
          </div>

          {/* The statement. */}
          <div className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div className="border-b border-gray-100 px-4 py-2">
              <h3 className="text-sm font-semibold text-gray-700">Every movement, oldest first</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-3 py-2">Date</th>
                    <th className="px-3 py-2">What</th>
                    {multi ? <th className="px-3 py-2">Code</th> : null}
                    <th className="px-3 py-2">Reference</th>
                    <th className="px-3 py-2">Booked by</th>
                    <th className="px-3 py-2 text-right">Qty</th>
                    <th className="px-3 py-2 text-right">Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {timeline.map((r) => (r.type === 'count' ? (
                    <tr key={`c${r.c.count_id}`} className="bg-amber-50/60">
                      <td className="whitespace-nowrap px-3 py-2 tabular-nums text-gray-600">{when(r.c.submitted_at)}</td>
                      <td colSpan={multi ? 4 : 3} className="px-3 py-2 text-gray-800">
                        <span className="font-semibold">Week {r.c.count_week} count</span>
                        <span className="text-gray-500"> by {r.c.counted_by || '—'}: counted {num(r.c.counted)}, ledger said {num(r.c.ledger_at_count)}</span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums"><Diff value={r.c.difference} /></td>
                      <td className="px-3 py-2 text-right text-xs text-gray-400">check</td>
                    </tr>
                  ) : (
                    <tr key={r.m.id} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-3 py-2 tabular-nums text-gray-600">
                        {r.m.count_day ? (
                          <>
                            {r.m.count_day.split('-').reverse().join('/')}
                            <span className="block text-[11px] text-gray-400">end of count day, booked {ukDay(r.m.booked_at)}</span>
                          </>
                        ) : when(r.m.at)}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${TONE[r.m.kind] || 'bg-gray-100 text-gray-700'}`}>
                          {r.m.label}
                        </span>
                        {r.m.counted !== null ? <span className="ml-1.5 text-xs text-gray-500">counted {num(r.m.counted)}</span> : null}
                        {r.m.kind === 'manual' || r.m.kind === 'grn_correction' || r.m.kind === 'issue_reversed' || r.m.kind === 'seed_correction' ? (
                          <span className="block text-xs text-gray-500">{r.m.note}</span>
                        ) : null}
                      </td>
                      {multi ? <td className="px-3 py-2 font-mono text-xs text-gray-500">{r.m.item_code}</td> : null}
                      <td className="px-3 py-2 text-gray-700">
                        <span className="font-mono text-xs">{r.m.reference || '—'}</span>
                        {r.m.detail ? <span className="block text-xs text-gray-500">{r.m.detail}</span> : null}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">{r.m.by || '—'}</td>
                      <td className={`px-3 py-2 text-right font-semibold tabular-nums ${r.m.qty < 0 ? 'text-gray-900' : 'text-emerald-700'}`}>
                        {signed(r.m.qty)}
                      </td>
                      <td className={`px-3 py-2 text-right tabular-nums ${r.m.balance < 0 ? 'font-semibold text-amber-700' : 'text-gray-900'}`}>
                        {num(r.m.balance)}
                      </td>
                    </tr>
                  )))}
                  {!timeline.length ? (
                    <tr><td colSpan={multi ? 7 : 6} className="px-3 py-6 text-center text-xs text-gray-400">No movements recorded for this item here.</td></tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}
    </Shell>
  );
}

// ------------------------------------------------------------------ VAN

const VAN_LABEL = {
  receipt: 'Received onto van', usage: 'Used on job', faulty: 'Faulty from stores', adjustment: 'Adjustment',
};

function VanHistory({ registration, stockItemId, code, name, onClose }) {
  const { data, isLoading, isError, error } = useVanItemHistory(registration, stockItemId);

  // The API pages newest first and hands back the balance before the oldest
  // row it sent, so the running figures are rebuilt forwards from there.
  const rows = useMemo(() => {
    if (!data) return [];
    let bal = Number(data.balance_before_page);
    return [...data.movements].reverse().map((m, i) => {
      const delta = m.kind === 'receipt' || m.kind === 'adjustment' ? Number(m.qty) : -Number(m.qty);
      bal += delta;
      return { ...m, i, delta, balance: bal };
    });
  }, [data]);

  const csv = () => {
    const lines = [['Date', 'What', 'Reference', 'Detail', 'Qty', 'Balance']];
    lines.push(['', 'Opening count', '', '', '', data.opening]);
    rows.forEach((r) => lines.push([when(r.at), VAN_LABEL[r.kind] || r.kind, r.ref, r.detail, r.delta, r.balance]));
    download(`van-history-${registration}-${code}.csv`, lines);
  };

  const partial = data && data.total > data.movements.length;

  return (
    <Shell title={`${code || ''} · ${name || ''}`} subtitle={`Van ${registration}`} onClose={onClose} onCsv={data ? csv : null}>
      {isLoading ? <p className="py-10 text-center text-sm text-gray-400">Loading history…</p> : null}
      {isError ? (
        <p className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
          {error?.response?.data?.error || 'The history could not be loaded.'}
        </p>
      ) : null}
      {data ? (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <div className="border-b border-gray-100 px-4 py-2">
            <h3 className="text-sm font-semibold text-gray-700">Since the van&apos;s baseline count</h3>
            <p className="text-xs text-gray-400">
              Opening count {num(data.opening)}
              {partial ? ` · showing the latest ${num(data.movements.length)} of ${num(data.total)} movements` : ''}
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">What</th>
                  <th className="px-3 py-2">Reference</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2 text-right">Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {!partial ? (
                  <tr className="bg-gray-50/60">
                    <td className="px-3 py-2 text-gray-500">—</td>
                    <td className="px-3 py-2"><span className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${TONE.opening}`}>Opening count</span></td>
                    <td className="px-3 py-2" />
                    <td className="px-3 py-2" />
                    <td className="px-3 py-2 text-right tabular-nums text-gray-900">{num(data.opening)}</td>
                  </tr>
                ) : null}
                {rows.map((r) => (
                  <tr key={r.i} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-3 py-2 tabular-nums text-gray-600">{when(r.at)}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${TONE[r.kind] || 'bg-gray-100 text-gray-700'}`}>
                        {VAN_LABEL[r.kind] || r.kind}
                      </span>
                      {r.discipline ? <span className="ml-1.5 text-xs text-gray-400">{r.discipline}</span> : null}
                    </td>
                    <td className="px-3 py-2 text-gray-700">
                      <span className="font-mono text-xs">{r.ref || '—'}</span>
                      {r.detail ? <span className="block text-xs text-gray-500">{r.detail}</span> : null}
                    </td>
                    <td className={`px-3 py-2 text-right font-semibold tabular-nums ${r.delta < 0 ? 'text-gray-900' : 'text-emerald-700'}`}>{signed(r.delta)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${r.balance < 0 ? 'font-semibold text-amber-700' : 'text-gray-900'}`}>{num(r.balance)}</td>
                  </tr>
                ))}
                {!rows.length ? (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-xs text-gray-400">Nothing has moved on this item since the count.</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </Shell>
  );
}

export default function StockHistoryPanel({ target, onClose }) {
  if (!target) return null;
  return (
    <Fragment>
      {target.mode === 'storeroom' ? (
        <StoreroomHistory storeroomId={target.storeroomId} codes={target.codes} name={target.name} onClose={onClose} />
      ) : (
        <VanHistory
          registration={target.registration}
          stockItemId={target.stockItemId}
          code={target.code}
          name={target.name}
          onClose={onClose}
        />
      )}
    </Fragment>
  );
}
